// Barcode scanner: use native BarcodeDetector API when available (fast, accurate),
// otherwise fall back to Quagga2. MacroFactor uses native mobile APIs; on web
// BarcodeDetector is the closest—Chrome/Edge/Android support it.

import React, { useEffect, useRef, useState } from "react";

const MIN_BARCODE_LENGTH = 8;
const FOOD_FORMATS = ["ean_13", "ean_8", "upc_a", "upc_e", "code_128"] as const;

/** EAN-13 / UPC-A check digit validation (for Quagga fallback). */
function isValidCheckDigit(code: string): boolean {
  const digits = code.replace(/\D/g, "");
  const len = digits.length;
  if (len !== 12 && len !== 13) return true;
  const d = digits.split("").map(Number);
  let sum = 0;
  for (let i = 0; i < len - 1; i++) sum += d[i] * (i % 2 === 0 ? 1 : 3);
  return d[len - 1] === (10 - (sum % 10)) % 10;
}

// ─── Native BarcodeDetector path (fast, like MacroFactor on web) ───
function useNativeScanner(
  containerRef: React.RefObject<HTMLDivElement | null>,
  active: boolean,
  onScan: (barcode: string) => void,
  onError?: (message: string) => void
) {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const loopRef = useRef<number | null>(null);
  const lastCodeRef = useRef<string | null>(null);
  const lastTimeRef = useRef(0);

  useEffect(() => {
    if (!active || !containerRef.current) return;

    const video = document.createElement("video");
    video.setAttribute("playsinline", "true");
    video.setAttribute("autoplay", "true");
    video.setAttribute("muted", "true");
    video.style.cssText = "position:absolute;top:0;left:0;width:100%;height:100%;object-fit:cover;";
    containerRef.current.appendChild(video);
    videoRef.current = video;

    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment", width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        video.srcObject = stream;
        await video.play();

        const BarcodeDetector = (window as unknown as { BarcodeDetector?: typeof globalThis.BarcodeDetector }).BarcodeDetector;
        if (!BarcodeDetector) {
          setError("BarcodeDetector not supported in this browser.");
          return;
        }
        const detector = new BarcodeDetector({ formats: [...FOOD_FORMATS] });
        setReady(true);
        setError(null);

        const SCAN_INTERVAL_MS = 80;
        const SAME_CODE_MS = 200;
        const REQUIRED_SAME = 2;

        async function scanLoop() {
          if (cancelled || video.readyState < 2) {
            loopRef.current = window.setTimeout(scanLoop, SCAN_INTERVAL_MS);
            return;
          }
          try {
            const barcodes = await detector.detect(video);
            if (cancelled) return;
            const code = barcodes[0]?.rawValue?.trim();
            if (code && code.length >= MIN_BARCODE_LENGTH) {
              const now = Date.now();
              if (code === lastCodeRef.current && now - lastTimeRef.current <= SAME_CODE_MS) {
                lastCodeRef.current = null;
                onScan(code);
                return;
              }
              lastCodeRef.current = code;
              lastTimeRef.current = now;
            }
          } catch {
            // ignore single-frame errors
          }
          loopRef.current = window.setTimeout(scanLoop, SCAN_INTERVAL_MS);
        }
        scanLoop();
      } catch (e) {
        if (!cancelled) {
          const raw = e instanceof Error ? e.message : String(e);
          // Translate technical errors into user-friendly messages
          let msg: string;
          if (!navigator.mediaDevices || raw.toLowerCase().includes("not defined")) {
            msg = "Camera not available. Please allow camera access in Settings → Privacy & Security → Camera → Ingrediable.";
          } else if (raw.toLowerCase().includes("denied") || raw.toLowerCase().includes("permission") || raw.toLowerCase().includes("notallowed")) {
            msg = "Camera access denied. Go to Settings → Privacy & Security → Camera → Ingrediable to enable it.";
          } else if (raw.toLowerCase().includes("notfound") || raw.toLowerCase().includes("no camera")) {
            msg = "No camera found on this device.";
          } else {
            msg = "Could not access camera. Please check your permissions in Settings.";
          }
          setError(msg);
          onError?.(msg);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (loopRef.current) clearTimeout(loopRef.current);
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      videoRef.current?.remove();
      videoRef.current = null;
      setReady(false);
    };
  }, [active, onScan, onError]);

  return { ready, error };
}

// ─── Quagga2 fallback ───
type Quagga = {
  init: (config: unknown, cb: (err: Error | null) => void) => void;
  start: () => void;
  stop: () => void;
  onDetected: (cb: (data: { codeResult: { code: string } }) => void) => void;
  offDetected: (cb?: (...args: unknown[]) => void) => void;
};

const QUAGGA_CONFIG = {
  inputStream: {
    name: "Live",
    type: "LiveStream" as const,
    constraints: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: "environment" },
    area: { top: "25%", right: "25%", left: "25%", bottom: "25%", borderColor: "rgba(0, 255, 0, 0.6)", borderWidth: 2 },
  },
  frequency: 8,
  locator: { patchSize: "medium", halfSample: true },
  numOfWorkers: 0,
  decoder: { readers: ["ean_reader", "ean_8_reader", "upc_reader", "upc_e_reader", "code_128_reader"] },
  locate: false,
};

function useQuaggaScanner(
  containerRef: React.RefObject<HTMLDivElement | null>,
  active: boolean,
  onScan: (barcode: string) => void,
  onError?: (message: string) => void
) {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handlerRef = useRef<((data: unknown) => void) | null>(null);

  useEffect(() => {
    if (!active || !containerRef.current) return;

    let Quagga: Quagga | null = null;
    let matchCode: string | null = null;
    let matchCount = 0;
    let firstMatchTime = 0;
    const REQUIRED = 3;
    const WINDOW_MS = 800;

    const handler = (data: { codeResult?: { code?: string } } | Array<{ codeResult?: { code?: string } }>) => {
      const single = Array.isArray(data) ? data[0] : data;
      const code = single?.codeResult?.code?.trim();
      if (!code || code.length < MIN_BARCODE_LENGTH || !isValidCheckDigit(code)) return;
      const now = Date.now();
      if (code === matchCode) {
        matchCount++;
        if (matchCount >= REQUIRED && now - firstMatchTime <= WINDOW_MS) {
          matchCode = null;
          matchCount = 0;
          Quagga!.stop();
          onScan(code);
        }
        return;
      }
      matchCode = code;
      matchCount = 1;
      firstMatchTime = now;
    };
    handlerRef.current = handler;

    (async () => {
      try {
        const mod = await import("@ericblade/quagga2");
        Quagga = mod.default as Quagga;
      } catch {
        setError("Scanner library failed to load.");
        onError?.("Scanner library failed to load.");
        return;
      }
      if (!containerRef.current || !Quagga) return;

      Quagga.init(
        { ...QUAGGA_CONFIG, inputStream: { ...QUAGGA_CONFIG.inputStream, target: containerRef.current } },
        (err: Error | null) => {
          if (err) {
            const msg = err?.message || "Could not access camera.";
            setError(msg);
            onError?.(msg);
            return;
          }
          setError(null);
          Quagga!.onDetected(handler);
          Quagga!.start();
          setReady(true);
        }
      );
    })();

    return () => {
      if (Quagga && handlerRef.current) {
        Quagga.offDetected(handlerRef.current);
        Quagga.stop();
      }
      setReady(false);
    };
  }, [active, onScan, onError]);

  return { ready, error };
}

// ─── Component ───
type Props = {
  onScan: (barcode: string) => void;
  onError?: (message: string) => void;
  active: boolean;
  className?: string;
  style?: React.CSSProperties;
};

export function BarcodeScanner({ onScan, onError, active, className, style }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [useNative, setUseNative] = useState<boolean | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const win = window as unknown as { BarcodeDetector?: unknown };
    if (win.BarcodeDetector) {
      setUseNative(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { BarcodeDetectorPolyfill } = await import("@undecaf/barcode-detector-polyfill");
        if (cancelled) return;
        win.BarcodeDetector = BarcodeDetectorPolyfill;
        setUseNative(true);
      } catch {
        if (!cancelled) setUseNative(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const native = useNativeScanner(containerRef, active && useNative === true, onScan, onError);
  const quagga = useQuaggaScanner(containerRef, active && useNative === false, onScan, onError);

  const ready = useNative === true ? native.ready : useNative === false ? quagga.ready : false;
  const error = useNative === true ? native.error : useNative === false ? quagga.error : null;

  if (!active) return null;

  return (
    <div className={className} style={{ position: "relative", overflow: "hidden", borderRadius: 16, ...style }}>
      <div
        ref={containerRef}
        className="barcode-scanner-viewport"
        style={{
          position: "relative",
          width: "100%",
          minHeight: 280,
          height: 320,
          background: "#111",
          overflow: "hidden",
        }}
      />
      <style>{`
        .barcode-scanner-viewport video,
        .barcode-scanner-viewport canvas {
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
          width: 100% !important;
          height: 100% !important;
          object-fit: cover;
        }
        .barcode-scanner-viewport canvas.drawingBuffer { pointer-events: none; }
      `}</style>
      {error && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(0,0,0,0.7)",
            color: "#fff",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            textAlign: "center",
          }}
        >
          <span style={{ fontSize: 14 }}>{error}</span>
          <span style={{ fontSize: 12, marginTop: 8, opacity: 0.8 }}>Allow camera access and try again.</span>
        </div>
      )}
      {ready && !error && (
        <div
          style={{
            position: "absolute",
            bottom: 12,
            left: "50%",
            transform: "translateX(-50%)",
            fontSize: 12,
            color: "rgba(255,255,255,0.9)",
            textAlign: "center",
          }}
        >
          Hold barcode in frame
        </div>
      )}
    </div>
  );
}
